import supabase from './supabase/supabase';

/**
 * Custom fetcher for Supabase Auth operations
 */
export const authFetch = async (endpoint, options = {}) => {
  try {
    if (endpoint === 'get-session') {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return { status: 'success', data: { user: data.session?.user || null } };
    }

    if (endpoint === 'sign-out') {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { status: 'success' };
    }

    if (endpoint === 'sign-in/email') {
      const body = JSON.parse(options.body || '{}');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: body.email,
        password: body.password,
      });
      if (error) throw error;
      return { status: 'success', data };
    }

    if (endpoint === 'sign-up/email') {
      const body = JSON.parse(options.body || '{}');
      const { data, error } = await supabase.auth.signUp({
        email: body.email,
        password: body.password,
        options: {
          data: {
            name: body.name,
          }
        }
      });
      if (error) throw error;
      return { status: 'success', data };
    }

    throw new Error('Auth action not supported');
  } catch (error) {
    console.error(`Auth API Error (${endpoint}):`, error);
    return { status: 'error', error: error.message || 'Erro na autenticação' };
  }
};

/**
 * Custom fetcher for Supabase operations
 */
export const dataFetch = async (endpoint, options = {}) => {
  const [action, ...rest] = endpoint.split('/');
  const tableWithParams = rest.join('/');
  const [tableName, queryString] = tableWithParams.split('?');
  
  try {
    // 1. SELECT / READ
    if (action === 'read') {
      let query = supabase.from(tableName).select('*');
      
      if (queryString) {
        const params = new URLSearchParams(queryString);
        for (const [key, value] of params.entries()) {
          if (key === 'sort') {
            const order = params.get('order') || 'desc';
            query = query.order(value, { ascending: order === 'asc' });
          } else if (key === 'order') {
            // Already handled by sort
          } else if (key === 'limit') {
            query = query.limit(parseInt(value));
          } else if (key.endsWith('[like]')) {
            const realKey = key.replace('[like]', '');
            query = query.like(realKey, `%${value}%`);
          } else if (key.endsWith('[ilike]')) {
            const realKey = key.replace('[ilike]', '');
            query = query.ilike(realKey, `%${value}%`);
          } else if (key.endsWith('[is]')) {
             const realKey = key.replace('[is]', '');
             if (value === 'null') {
               query = query.is(realKey, null);
             } else {
               query = query.is(realKey, value);
             }
          } else {
            query = query.eq(key, value);
          }
        }
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return { status: 'success', data };
    }

    // 2. INSERT / CREATE
    if (action === 'create') {
      const body = JSON.parse(options.body || '{}');
      const { data, error } = await supabase.from(tableName).insert([body]).select();
      if (error) throw error;
      return { status: 'success', data: data?.[0] };
    }

    // 3. UPDATE
    if (action === 'update') {
      const [table, id] = tableWithParams.split('/');
      if (!id || id === 'null' || id === 'undefined' || id.length < 10) {
        throw new Error(`Invalid ID provided for update: ${id}`);
      }

      const body = JSON.parse(options.body || '{}');
      const { data, error } = await supabase
        .from(table)
        .update(body)
        .eq('id', id)
        .select();
        
      if (error) throw error;
      return { status: 'success', data: data?.[0] };
    }

    // 4. DELETE
    if (action === 'delete') {
      const [table, id] = tableWithParams.split('/');
      if (!id || id === 'null' || id === 'undefined') {
        throw new Error(`Invalid ID provided for delete: ${id}`);
      }
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      return { status: 'success' };
    }

    // 5. RPC
    if (action === 'rpc') {
      const body = JSON.parse(options.body || '{}');
      const { data, error } = await supabase.rpc(tableName, body);
      if (error) throw error;
      return { status: 'success', data };
    }

    throw new Error('Action not supported');
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    return { status: 'error', error: error.message || 'Erro na requisição' };
  }
};